import { useQuery } from "@tanstack/react-query";
import { UseFormReturn, useWatch } from "react-hook-form";
import { getProduct } from "../../core/product.core";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import CloseIcon from "../../icons/iconClose.comp";
import ProductSearchInput from "../product/productSearchInput.core";
import SolidWeakBtn from "../form/solidWeakBtn.comp";
import { RefObject, useRef } from "react";

export default function MultipleProductsSelector({
  methods,
  fieldName,
  showVariants,
  buttonText,
  btnId,
}: {
  methods: UseFormReturn<any, any>;
  fieldName: string;
  showVariants?: boolean;
  buttonText?: string;
  btnId?: string;
}) {
  const { t, msg } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  function deleteProduct(productId: string) {
    const proudctIds = methods.getValues(fieldName);

    const set = new Set(proudctIds);
    set.delete(productId);
    methods.setValue(fieldName, Array.from(set));
  }

  return (
    <div id="attach-to-products" className="space-y-2">
      {/* <label>{t(msg.discount.attachToProducts)}</label> */}
      <AttachBadges methods={methods} handleDelete={deleteProduct} fieldName={fieldName} />
      <div className="flex">
        <button
          type="button"
          id={btnId}
          className="btn bg-her-blue px-4 py-2 rounded-md text-white"
          onClick={() => dialogRef.current?.showModal()}
        >
          {buttonText ?? t(msg.product.add)}
        </button>
      </div>
      <ProductSelectorDialog
        dialogRef={dialogRef}
        methods={methods}
        fieldName={fieldName}
        deleteProduct={deleteProduct}
        showVariants={showVariants}
      />
    </div>
  );
}

const AttachBadges = ({
  methods,
  fieldName,
  handleDelete,
}: {
  fieldName: any;
  methods: UseFormReturn<any, any>;
  handleDelete: (productId: string) => void;
}) => {
  const attachIds = useWatch({
    control: methods.control,
    name: fieldName,
  });

  if (!attachIds) return null;

  return (
    <div className="flex gap-2 flex-wrap">
      {attachIds.map((p) => (
        <AttachProductBadge key={p} attachId={p} handleDelete={handleDelete} />
      ))}
    </div>
  );
};

const AttachProductBadge = ({ attachId, handleDelete }: { attachId: string; handleDelete: (attachId: string) => void }) => {
  const { data: product, isFetched: productIsFetched } = useQuery({
    queryKey: ["productSingle", attachId],
    queryFn: (context) => getProduct(context.queryKey[1]),
    enabled: !!attachId,
  });

  if (!productIsFetched) return null;

  return (
    <div className="flex items-center px-5 py-2.5 text-sm font-medium text-center text-her-blue border border-her-blue rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300">
      {localeCore.getLocaleValueDefaultFallback(product?.name)}
      <button
        className="flex items-center justify-center w-4 h-4 ml-2 text-xs font-semibold text-blue-800 bg-blue-200 rounded-full hover:bg-red-400 hover:text-white"
        onClick={(e) => {
          e.preventDefault();
          handleDelete(attachId);
        }}
      >
        <CloseIcon />
      </button>
    </div>
  );
};

interface ProductSelectorDialogProps {
  dialogRef: RefObject<HTMLDialogElement>;
  methods: UseFormReturn<any, any>;
  fieldName: string;
  deleteProduct: (productId: string) => void;
  showVariants?: boolean;
}
function ProductSelectorDialog({ dialogRef, methods, fieldName, deleteProduct, showVariants }: ProductSelectorDialogProps) {
  function addProduct(productId: string) {
    const proudctIds = methods.getValues(fieldName);

    const set = new Set(proudctIds);
    set.add(productId);
    methods.setValue(fieldName, Array.from(set));
  }

  return (
    <dialog
      id="add-multiple-product-dialog"
      ref={dialogRef}
      className="dark:bg-her-blackOlive dark:text-white p-10 rounded-lg w-[80vw] max-w-[700px] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      {/* <h1 className="text-xl mb-4">{t(msg.delivery.assign)}</h1> */}
      <div className="flex flex-col gap-2">
        <ProductSearchInput addProduct={addProduct} showVariants={showVariants} />
        <AttachBadges methods={methods} handleDelete={deleteProduct} fieldName={fieldName} />
      </div>
      <div className="flex gap-4 mt-6">
        <SolidWeakBtn id="close-delete-gift-promotion" type="button" onClick={() => dialogRef.current?.close()}>
          Close
        </SolidWeakBtn>
      </div>
    </dialog>
  );
}
